import { useCallback, useEffect, useRef, useState } from 'react';
import { schwachstellenService } from '../services/schwachstellenService';
import type { SchwachstelleLockInfo } from '../types';

const HEARTBEAT_INTERVAL_MS = 30_000;
const VIEWER_POLL_INTERVAL_MS = 10_000;

interface UseSchwachstelleLockOptions {
  schwachstelleId: number | null;
  enabled: boolean;
  onLockLost?: () => void;
}

/**
 * Hook that manages a pessimistic edit lock on a Schwachstelle.
 *
 * When `enabled` is true (lock holder):
 *   - Sends heartbeats every 30s to keep the lock alive.
 *   - Tracks `expiresAt` so the UI can show a countdown.
 *   - Releases the lock on unmount / when `enabled` turns false.
 *
 * Returns `releaseLock` for explicit release and `expiresAt` for countdown display.
 */
export function useSchwachstelleLock({ schwachstelleId, enabled, onLockLost }: UseSchwachstelleLockOptions) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lockActiveRef = useRef(false);
  const onLockLostRef = useRef(onLockLost);
  onLockLostRef.current = onLockLost;

  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const setInitialExpiresAt = useCallback((value: string) => {
    setExpiresAt(value);
  }, []);

  const releaseLock = useCallback(async () => {
    if (!schwachstelleId || !lockActiveRef.current) return;
    lockActiveRef.current = false;
    setExpiresAt(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    try {
      await schwachstellenService.unlock(schwachstelleId);
    } catch {
      // Best-effort unlock; server timeout will clean up
    }
  }, [schwachstelleId]);

  useEffect(() => {
    if (!enabled || !schwachstelleId) {
      return;
    }

    lockActiveRef.current = true;

    intervalRef.current = setInterval(async () => {
      if (!lockActiveRef.current || !schwachstelleId) return;
      try {
        const res = await schwachstellenService.heartbeat(schwachstelleId);
        setExpiresAt(res.expires_at);
      } catch {
        lockActiveRef.current = false;
        setExpiresAt(null);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        onLockLostRef.current?.();
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (lockActiveRef.current && schwachstelleId) {
        lockActiveRef.current = false;
        schwachstellenService.unlock(schwachstelleId).catch(() => {});
      }
    };
  }, [enabled, schwachstelleId]);

  // beforeunload handler: release lock when tab/window closes
  useEffect(() => {
    if (!enabled || !schwachstelleId) return;

    const handleBeforeUnload = () => {
      if (schwachstelleId && lockActiveRef.current) {
        navigator.sendBeacon?.(`/api/schwachstellen/${schwachstelleId}/unlock`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, schwachstelleId]);

  return { releaseLock, expiresAt, setInitialExpiresAt };
}

/* ── Viewer-side lock polling ───────────────────────────────────────── */

interface UseLockViewerOptions {
  schwachstelleId: number | null;
  lockInfo: SchwachstelleLockInfo | null;
  currentUserId: number | undefined;
  onLockReleased: () => void;
}

/**
 * Polls the lock status every 10s when a Schwachstelle is locked by another user.
 * Calls `onLockReleased` when the lock disappears so the UI can update immediately.
 */
export function useLockViewerPolling({ schwachstelleId, lockInfo, currentUserId, onLockReleased }: UseLockViewerOptions) {
  const onReleasedRef = useRef(onLockReleased);
  onReleasedRef.current = onLockReleased;

  useEffect(() => {
    const isLockedByOther = !!lockInfo && lockInfo.locked_by.id !== currentUserId;
    if (!isLockedByOther || !schwachstelleId) return;

    const interval = setInterval(async () => {
      try {
        const data = await schwachstellenService.get(schwachstelleId);
        if (!data.lock_info) {
          onReleasedRef.current();
        }
      } catch {
        // Ignore polling errors
      }
    }, VIEWER_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [schwachstelleId, lockInfo, currentUserId]);
}
